import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { fetchCameras } from "../lib/api";
import { useCounty } from "../lib/CountyContext";
import CameraCard from "../components/CameraCard";
import CameraViewerModal from "../components/CameraViewerModal";
import MapSearchControl from "../components/map/MapSearchControl";
import { VideoCamera, MapTrifold, ListBullets } from "@phosphor-icons/react";

export default function Cameras() {
  const { county, current, setCounty } = useCounty();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("county"); // 'county' or 'all'
  const [activeCamera, setActiveCamera] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchCameras(scope === "all" ? undefined : county)
      .then(setCameras)
      .finally(() => setLoading(false));
  }, [county, scope]);

  const center = useMemo(() => {
    if (cameras.length === 0) return [47.7, -120.5];
    const lat = cameras.reduce((a, c) => a + c.lat, 0) / cameras.length;
    const lon = cameras.reduce((a, c) => a + c.lon, 0) / cameras.length;
    return [lat, lon];
  }, [cameras]);

  return (
    <div data-testid="cameras-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-[15px] uppercase tracking-[0.18em] font-bold text-tac-faint mb-2">
            WA DNR / Pano AI Wildfire Watch
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight text-tac-text">
            Wildfire Cameras
          </h1>
          <p className="text-[15px] text-tac-dim mt-1.5 max-w-xl">
            Public Pano AI cameras deployed across Washington for early wildfire detection.
            Click any camera card to see details and open the live feed in a focused popup window.
          </p>
        </div>
        <div className="flex gap-1">
          <button
            data-testid="cameras-scope-county"
            onClick={() => setScope("county")}
            className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
              scope === "county"
                ? "bg-tac-primary text-white border-tac-primary"
                : "bg-tac-surface text-tac-dim border-tac-border hover:text-tac-text"
            }`}
          >
            {current?.name} Co.
          </button>
          <button
            data-testid="cameras-scope-all"
            onClick={() => setScope("all")}
            className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
              scope === "all"
                ? "bg-tac-primary text-white border-tac-primary"
                : "bg-tac-surface text-tac-dim border-tac-border hover:text-tac-text"
            }`}
          >
            Statewide
          </button>
        </div>
      </div>

      {loading ? (
        <>
          <div className="scan-line" />
          <div className="text-center py-12 text-[15px] text-tac-dim font-mono-tac uppercase tracking-widest">Loading…</div>
        </>
      ) : cameras.length === 0 ? (
        <div className="text-center py-16 text-[15px] text-tac-faint">
          <VideoCamera size={28} weight="duotone" className="mx-auto mb-3 text-tac-borderBright" />
          No cameras in {current?.name} County. Try switching to <button data-testid="switch-statewide" onClick={() => setScope("all")} className="text-tac-primary underline">Statewide</button>.
        </div>
      ) : (
        <>
          <div className="border border-tac-border rounded-sm overflow-hidden h-[280px] sm:h-[360px] mb-5" data-testid="cameras-map">
            <MapContainer center={center} zoom={scope === "all" ? 7 : 8} className="h-full w-full" style={{ background: "#0F1311" }}>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
                attribution="Tiles &copy; Esri"
              />
              <MapSearchControl />
              {cameras.map((cam) => (
                <CircleMarker
                  key={cam.id}
                  center={[cam.lat, cam.lon]}
                  radius={6}
                  pathOptions={{ color: "#0A0D0C", weight: 2, fillColor: "#457B9D", fillOpacity: 0.92 }}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="font-display font-bold text-sm text-tac-text mb-1">{cam.name}</div>
                      <div className="text-[14px] text-tac-dim mb-2">{cam.description}</div>
                      <button
                        type="button"
                        onClick={() => setActiveCamera(cam)}
                        data-testid={`map-popup-open-${cam.id}`}
                        className="text-[11px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
                      >
                        Open Camera →
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cameras.map((cam) => (
              <CameraCard key={cam.id} camera={cam} />
            ))}
          </div>

          <div className="mt-6 p-4 border border-tac-border bg-tac-surface/40 text-[15px] text-tac-dim">
            <strong className="text-tac-text">Note:</strong> The WA DNR Wildfire Watch viewer
            sets <code className="font-mono-tac">frame-ancestors 'none'</code>, which blocks
            iframe embedding. Each camera opens the official live feed in a focused popup window
            sized for viewing. Coordinates are approximate.
          </div>
        </>
      )}
      {activeCamera && (
        <CameraViewerModal camera={activeCamera} onClose={() => setActiveCamera(null)} />
      )}
    </div>
  );
}
